// Runtime configuration for the React SPA.

// The browser only talks to the origin that served the SPA. Vite (dev and
// preview) and the production web server forward /api and /storage to Laravel.
// The API origin is deliberately not configurable here: baking it into the
// bundle would make the browser call it directly and re-introduce CORS.

/** Explicit tenant host override (X-Tenant-Host). Falls back to window.location.host. */
export const TENANT_HOST = (import.meta.env.VITE_TENANT_HOST ?? '').trim();

/** Apex domain that agency subdomains hang off (e.g. `fleetava.ma`). */
export const TENANT_BASE_DOMAIN = String(import.meta.env.VITE_TENANT_BASE_DOMAIN ?? 'localhost')
  .trim()
  .toLowerCase()
  .replace(/^\.+|\.+$/g, '');

/** Hosts that serve the SaaS landing page instead of an agency site. */
export const PLATFORM_HOSTS = String(import.meta.env.VITE_PLATFORM_HOSTS ?? 'localhost,127.0.0.1')
  .split(',')
  .map((entry) => entry.trim().toLowerCase())
  .filter((entry) => entry !== '');

export const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === 'true';

function currentHostname(): string {
  if (typeof window === 'undefined') return '';
  return window.location.hostname.trim().toLowerCase().replace(/\.$/, '');
}

/**
 * The apex and explicit platform hosts render the marketing landing page;
 * agencies live on `<slug>.<baseDomain>` subdomains or their own custom domain.
 *
 * Setting `VITE_TENANT_HOST` is a developer telling us "show me that agency",
 * which also makes local development of an agency site possible without
 * DNS tricks (`VITE_TENANT_HOST=agadir.localhost npm run dev`).
 */
export function isPlatformHost(): boolean {
  if (TENANT_HOST) return false;
  const host = currentHostname();
  if (host === '') return true;
  if (PLATFORM_HOSTS.includes(host)) return true;
  return host === TENANT_BASE_DOMAIN;
}

export function currentTenantHost(): string {
  if (TENANT_HOST) return TENANT_HOST;
  if (typeof window !== 'undefined') return window.location.host;
  return '';
}

/**
 * Resolve a path stored by the API. Uploads live under `/storage`; rewrite an
 * absolute API URL to the same-origin path so images never hit the backend
 * host directly. External URLs without a storage path and app-local paths are
 * returned untouched.
 */
export function apiAsset(path: string): string {
  if (!path) return '';
  if (path.startsWith('data:') || path.startsWith('blob:')) return path;

  const storageIndex = path.indexOf('/storage/');
  if (storageIndex >= 0) return path.slice(storageIndex);
  if (path.startsWith('storage/')) return `/${path}`;

  return path;
}
